'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';

import {
  getAddressValidationRules,
  getCachedAddressValidationRules,
} from '@/lib/address/getAddressValidationRules';
import {
  getCountryAreaLabelKey,
  isCountryAreaRequired,
  isCountryAreaRequiredFallback,
  toCountryAreaOptions,
  type AddressValidationRulesData,
} from '@/lib/address/countryAreaRules';
import { getStatesForCountry } from '@/lib/checkout';

export interface CountryAreaFieldConfig {
  label: string;
  isRequired: boolean;
  options: Array<{ value: string; label: string }>;
  hasChoices: boolean;
  isLoading: boolean;
}

type RulesFetchStatus = 'idle' | 'loading' | 'ready' | 'error';

interface RulesFetchState {
  country: string;
  rules: AddressValidationRulesData | null;
  status: RulesFetchStatus;
}

function createInitialFetchState(countryCode: string): RulesFetchState {
  if (countryCode.length !== 2) {
    return { country: '', rules: null, status: 'idle' };
  }

  const cached = getCachedAddressValidationRules(countryCode);
  if (cached) {
    return { country: countryCode, rules: cached, status: 'ready' };
  }

  return { country: countryCode, rules: null, status: 'loading' };
}

export function useCountryAreaFieldConfig(countryCode: string | undefined): CountryAreaFieldConfig {
  const t = useTranslations('checkout');
  const normalizedCountry = countryCode?.trim().toUpperCase() ?? '';

  const [fetchState, setFetchState] = useState<RulesFetchState>(() =>
    createInitialFetchState(normalizedCountry)
  );

  useEffect(() => {
    if (normalizedCountry.length !== 2) {
      setFetchState({ country: '', rules: null, status: 'idle' });
      return;
    }

    const cached = getCachedAddressValidationRules(normalizedCountry);
    if (cached) {
      setFetchState({ country: normalizedCountry, rules: cached, status: 'ready' });
      return;
    }

    let active = true;
    setFetchState({ country: normalizedCountry, rules: null, status: 'loading' });

    getAddressValidationRules(normalizedCountry)
      .then((data) => {
        if (!active) {
          return;
        }
        setFetchState({
          country: normalizedCountry,
          rules: data,
          status: data ? 'ready' : 'error',
        });
      })
      .catch(() => {
        if (!active) {
          return;
        }
        setFetchState({ country: normalizedCountry, rules: null, status: 'error' });
      });

    return () => {
      active = false;
    };
  }, [normalizedCountry]);

  const isCountryMismatch =
    normalizedCountry.length === 2 && fetchState.country !== normalizedCountry;

  const activeRules =
    fetchState.country === normalizedCountry && fetchState.status === 'ready'
      ? fetchState.rules
      : getCachedAddressValidationRules(normalizedCountry);

  const isLoading =
    normalizedCountry.length === 2 &&
    (isCountryMismatch ||
      (fetchState.country === normalizedCountry && fetchState.status === 'loading'));

  const labelKey = getCountryAreaLabelKey(activeRules?.countryAreaType);
  const label = t(`delivery.${labelKey}`);
  const isRequired =
    !isLoading &&
    (activeRules
      ? isCountryAreaRequired(activeRules.requiredFields)
      : isCountryAreaRequiredFallback(normalizedCountry));

  const options = useMemo(() => {
    const fromRules = toCountryAreaOptions(activeRules?.countryAreaChoices);
    if (fromRules.length > 0) {
      return fromRules;
    }

    return getStatesForCountry(normalizedCountry).map((state) => ({
      value: state.code,
      label: state.name,
    }));
  }, [activeRules?.countryAreaChoices, normalizedCountry]);

  return {
    label,
    isRequired,
    options,
    hasChoices: options.length > 0,
    isLoading,
  };
}
